import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { MarketList } from './components/MarketList';
import MarketCard from './components/MarketCard';
import { CreateMarket } from './components/CreateMarket';
import AdminDashboard from './components/AdminDashboard';
import Wallet from './components/Wallet';
import { HowItWorksModal } from './components/Modal';
import { WalletProvider } from './contexts/WalletProvider';
import { MarketProvider } from './contexts/MarketProvider';
import PrivyProviderWrapper from './contexts/PrivyProvider';
import { useAuth } from './hooks/useAuth';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname === '/admin';
  const { backendHealthy, handleLogout } = useAuth();
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    const updateViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  if (backendHealthy === false) {
    return (
      <div className="app">
        <div className="connection-error">
          <h2>Connection Lost</h2>
          <p>Unable to reach the IMO backend server.</p>
          <p>Please check your connection and try again.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (backendHealthy === null) {
    return (
      <div className="app">
        <div className="connection-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {!isAdminRoute && (
        <header className="app-header">
          <div className="header-left">
            <div className="brand-logo">
              <img 
                src="/assets/icons/imo-white.svg" 
                alt="FEEN" 
                className="brand-logo-img"
                onError={(e) => {
                  console.error('Logo failed to load, retrying...', e);
                  e.currentTarget.src = "/assets/icons/feen-header.svg?v=" + Date.now();
                }}
              />
            </div>
          </div>



          <div className="header-right">
            <button 
              className="how-it-works-button"
              onClick={() => setShowHowItWorks(true)}
            >
              [how it works]
            </button>
              <button 
                className="create-market-button"
                onClick={() => navigate('/create')}
              >
                Create Market
              </button>
            <button 
              className="header-action-button"
              onClick={() => window.open('https://x.com/imodotfun', '_blank')}
              title="Get support on X"
            >
              <img 
                src="/assets/icons/twitter-logo-white.png" 
                alt="X Support" 
                style={{ width: '20px', height: '20px' }}
              />
            </button>

            <div className="header-wallet">
              <Wallet onLogout={handleLogout} />
            </div>
          </div>
        </header>
      )}

      <main className={`app-main ${isAdminRoute ? 'admin-main' : ''}`}>
        <Routes>
          <Route path="/" element={<MarketList />} />
          <Route path="/create" element={<CreateMarket />} />
          <Route path="/market/:address" element={<MarketCard />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </main>

      <HowItWorksModal 
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      {process.env.NODE_ENV === 'production' && <Analytics />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <PrivyProviderWrapper>
        <WalletProvider>
          <MarketProvider>
            <AppLayout />
          </MarketProvider>
        </WalletProvider>
      </PrivyProviderWrapper>
    </Router>
  );
};

export default App;
